import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/ui/page-header';
import { useAuth } from '@/hooks/useAuth';
import { Zap, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export default function VectorShiftFileTest() {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [resumeText, setResumeText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [jobDescription, setJobDescription] = useState('');

  const testVectorShiftTextProcessing = async () => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter both resume text and job description.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setResults(null);

    try {
      setProgress(20);
      toast({
        title: "Starting text processing",
        description: "Sending your resume text directly to VectorShift..."
      });

      // Call the VectorShift function with text data
      const response = await supabase.functions.invoke('vectorshift-test', {
        body: {
          Resume_Text: resumeText,
          Job_Description: jobDescription,
          Additional_Instructions: "Please tailor this resume for maximum impact",
          Document_Types: ["resume", "cover-letter"]
        }
      });

      setProgress(80);

      if (response.error) {
        throw new Error(`VectorShift processing failed: ${response.error.message}`);
      }

      setResults(response.data);
      setProgress(100);

      toast({
        title: "Text processing complete! 🎉",
        description: "Your resume text was processed successfully!"
      });

    } catch (error) {
      console.error('VectorShift text test error:', error);
      toast({
        title: "Processing failed",
        description: error instanceof Error ? error.message : "Failed to process text with VectorShift",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
      <div className="container mx-auto py-8 space-y-6">
        <PageHeader
          title="VectorShift Text Processing Test"
          description="Test sending resume text directly to VectorShift - paste your text instead of uploading files!"
          onBack={() => window.history.back()}
        />

        {/* Status */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              TEXT INPUT TESTING
            </CardTitle>
          </CardHeader>
          <CardContent className="text-blue-700">
            <p>🔥 DIRECT TEXT PROCESSING</p>
            <p>🔥 PASTE YOUR RESUME TEXT BELOW</p>
            <p>🔥 NO FILE BULLSHIT</p>
            <p>🔥 STRAIGHT TO VECTORSHIFT</p>
          </CardContent>
        </Card>

        {/* Resume Text Input */}
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Enter Your Resume Text</CardTitle>
            <CardDescription>Paste your resume content here</CardDescription>
          </CardHeader>
          <CardContent>
            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste your resume text here..."
              className="w-full h-60 p-3 border rounded-lg resize-none font-mono text-sm"
            />
          </CardContent>
        </Card>

        {/* Job Description */}
        <Card>
          <CardHeader>
            <CardTitle>Step 2: Enter Job Description</CardTitle>
            <CardDescription>Paste the job description to tailor your resume against</CardDescription>
          </CardHeader>
          <CardContent>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job description here..."
              className="w-full h-40 p-3 border rounded-lg resize-none"
            />
          </CardContent>
        </Card>

        {/* Process Button */}
        {resumeText && jobDescription && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Step 3: Process Text with VectorShift
              </CardTitle>
              <CardDescription>Send your resume text directly to VectorShift for processing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={testVectorShiftTextProcessing}
                disabled={isProcessing}
                className="w-full"
                size="lg"
              >
                {isProcessing ? "Processing Text..." : "🚀 Process Resume Text"}
              </Button>

              {isProcessing && (
                <div className="space-y-2">
                  <Progress value={progress} className="w-full" />
                  <p className="text-sm text-muted-foreground text-center">
                    Processing: {progress}%
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Processing Results
              </CardTitle>
              <CardDescription>
                Results from direct text processing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Badge variant="outline" className="p-2">
                    ✅ Text processed successfully
                  </Badge>
                  <Badge variant="outline" className="p-2">
                    📄 {results.data?.Final_Resume_Text ? 'Resume generated' : 'Processing complete'}
                  </Badge>
                  <Badge variant="outline" className="p-2">
                    📧 {results.data?.Cover_Letter ? 'Cover letter created' : 'No cover letter'}
                  </Badge>
                  <Badge variant="outline" className="p-2">
                    📊 {results.data?.original_analysis ? 'Analysis complete' : 'Analysis pending'}
                  </Badge>
                </div>
                
                <pre className="whitespace-pre-wrap text-sm bg-muted p-4 rounded-lg overflow-auto max-h-96 font-mono">
                  {JSON.stringify(results, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}